import {
  DedicatedCategory,
  DedicatedGroup,
  DedicatedRelation,
  LoadRegistrationExpandResult,
  FrontendRegistration,
  FrontendRegistrationGroup,
} from "@timeedit/registration-schemas";
import {
  Mapping,
  UnsafeRecord,
  checkLinkedGroups,
  convertToString,
  createLookup,
  isDefined,
  presentRange,
  presentText,
} from "@timeedit/registration-shared";
import { Tag, Tooltip } from "@timeedit/ui-components";
import { Skeleton } from "@timeedit/ui-components/antd";
import dayjs from "dayjs";
import { isEmpty } from "lodash-es";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { CalendarEvent } from "../../../../utils/events";
import {
  LoadActions,
  RegistrationRequiredTranslations,
  GroupControlsSlotProps,
} from "../../types";
import {
  RangeReduce,
  displayAllConflicts,
  findActivityOwnGroup,
  groupControlsLabelledBy,
} from "../../utils";
import {
  getAllocationStatus,
  getDedicationData,
  isFull,
  isOpen,
} from "./ListViewColumnUtils";
import { TimeColumn } from "./TimeColumn";
import "./listViewColumns.scss";
import { AllocationControls } from "../../../AllocationControls";

type ExpandableComponentProps = {
  event: CalendarEvent;
  registration: FrontendRegistration;
  translations: RegistrationRequiredTranslations;
  mapping: Mapping;
  loadActions: LoadActions;
};
export function ExpandableComponent({
  event,
  registration,
  mapping,
  translations,
  loadActions,
}: ExpandableComponentProps): React.JSX.Element {
  const conflicts = displayAllConflicts({
    registration,
    event,
    mapping,
    translations,
  });
  const group = registration.groups[event.data.groupId];

  const objectsOnReservations = useMemo(() => {
    const objectIds = new Set<number>();
    for (const reservation of event.data.reservations) {
      reservation.objects?.forEach((o) => objectIds.add(o.objectId));
    }
    return Array.from(objectIds);
  }, [event.data.reservations]);

  return (
    <>
      <div>{conflicts}</div>
      <div
        className="resbox"
        data-reservation-ids={event.data.reservations.map((r) => r.id)}
      >
        {!event.noSchedule && (
          <>
            <div>{translations.day}:</div>
            <DayColumn events={[event]} />
            <div>{translations.time}:</div>
            <TimeColumn events={[event]} timezone={registration.timezone} />
            <div>{translations.dates}:</div>
            <DatesColumn events={[event]} />
          </>
        )}
        <TextColumn
          title={mapping.fieldname("activityType")}
          text={mapping.parse("activityType", group?.teObject)}
        />
        <TextColumn
          title={mapping.fieldname("groupName")}
          text={group?.label}
        />
        <LoadingExpandableComponent
          objectIds={objectsOnReservations}
          mapping={mapping}
          loadActions={loadActions}
        />
      </div>
    </>
  );
}

type LoadingExpandableComponentProps = {
  objectIds: number[];
  mapping: Mapping;
  loadActions: LoadActions;
};
function LoadingExpandableComponent({
  objectIds,
  mapping,
  loadActions,
}: LoadingExpandableComponentProps): React.JSX.Element {
  const [loading, setLoading] = useState(true);
  const [expandData, setExpandData] = useState<LoadRegistrationExpandResult>();

  useEffect(() => {
    let abort = false;

    loadActions
      .loadRegistrationExpand({ objectIds })
      .then((data) => {
        if (!abort) {
          setExpandData(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!abort) {
          setExpandData(undefined);
          setLoading(false);
        }
      });

    return () => {
      abort = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <>
        <SkeletonColumn />
        <SkeletonColumn />
      </>
    );
  }

  return (
    <>
      <TextColumn
        title={mapping.typename("staff")}
        text={expandData?.teachers}
      />
      <TextColumn title={mapping.typename("room")} text={expandData?.rooms} />
    </>
  );
}

function SkeletonColumn(): React.JSX.Element {
  return (
    <>
      <Skeleton.Input size={"small"} className={"column__skeleton--first"} />
      <Skeleton.Input size={"small"} />
    </>
  );
}

type TextColumnProps = {
  text: string | undefined;
  title: string;
};
type TextColumnReturn = React.JSX.Element | null;
function TextColumn({ title, text }: TextColumnProps): TextColumnReturn {
  if (!isDefined(text) || isEmpty(text)) return null;
  return (
    <>
      <div>{title}:</div>
      <div>{text}</div>
    </>
  );
}

export function DateColumn({
  events,
  autoWidth,
  translations,
}: {
  events: CalendarEvent[];
  autoWidth: boolean;
  translations: RegistrationRequiredTranslations;
}): React.JSX.Element {
  return (
    <div className="datecolumn">
      {events.map(({ start, data, noSchedule }, index) => {
        if (noSchedule) {
          return (
            <div key={`dayweekColumn-${data.groupId}-${index}`}>
              {translations.noSchedule}
            </div>
          );
        }
        return (
          <Fragment key={`dayweekColumn-${data.groupId}-${index}`}>
            <div>
              <span className={autoWidth ? "daywide" : ""}>
                {dayjs(start).format("dddd")}
              </span>
              {autoWidth && (
                <span className="daysmall">{dayjs(start).format("ddd")}</span>
              )}
            </div>
            <div className="timeColumn">
              {presentRange(
                data.actualStart.unix,
                data.actualEnd.unix,
                data.actualStart.timezone ??
                  Intl.DateTimeFormat().resolvedOptions().timeZone
              )}
            </div>
            <div>{RangeReduce.daysRangePresent(data.days)}</div>
          </Fragment>
        );
      }) ?? []}
    </div>
  );
}

function DayColumn({ events }: { events: CalendarEvent[] }): React.JSX.Element {
  return (
    <>
      {events.map(({ start, data }, index) => {
        return (
          <div key={`dayColumn-${data.groupId}-${index}`}>
            {dayjs(start).format("dddd")}
          </div>
        );
      }) ?? []}
    </>
  );
}

function DatesColumn({
  events,
}: {
  events: CalendarEvent[];
}): React.JSX.Element {
  return (
    <>
      {events.map(({ data }, index) => {
        const days = data.days ?? new Set<number>();
        return (
          <div key={`datesColumn-${data.groupId}-${index}`}>
            {RangeReduce.daysPresent(days)}
          </div>
        );
      }) ?? []}
    </>
  );
}

interface HiddenColumnProps {
  hidden: boolean;
  label: string;
}

export function HiddenColumn({
  hidden,
  label,
}: HiddenColumnProps): React.JSX.Element {
  if (!hidden) return <></>;
  return (
    <Tooltip title={label}>
      <Tag>{label}</Tag>
    </Tooltip>
  );
}

interface DedicatedColumnProps {
  dedicated: DedicatedGroup[];
  presentCategory: (data: DedicatedCategory[]) => string;
  presentRelation: (data: DedicatedRelation[]) => string;
}

export function DedicatedColumn({
  dedicated,
  presentCategory,
  presentRelation,
}: DedicatedColumnProps): React.JSX.Element {
  const data = useMemo(() => getDedicationData(dedicated), [dedicated]);

  const categoryText =
    data.category.length > 0 ? presentCategory(data.category) : "";
  const relationText =
    data.relation.length > 0 ? presentRelation(data.relation) : "";

  if (relationText === "" && categoryText === "") return <></>;

  return (
    <>
      {[categoryText, relationText].reduce(
        (textResult: React.JSX.Element[], text) => {
          if (text === "") return textResult;
          return [
            ...textResult,
            <Tooltip
              key={`${text}`}
              styles={{
                root: {
                  whiteSpace: "pre-line",
                },
              }}
              title={text}
            >
              <div>{presentText(text)}</div>
            </Tooltip>,
          ];
        },
        []
      )}
    </>
  );
}

export function AllocationControlColumn({
  hasConflict,
  activity,
  groups,
  registration,
  translations,
  mapping,
  allocationControlActions,
  mode,
}: GroupControlsSlotProps): React.JSX.Element {
  const groupIds = useMemo(() => groups.map((g) => g.id), [groups]);

  // Allocation to a linked group will happen on the server side
  // so just allocating to one is enough.
  const group = useMemo(
    () => findActivityOwnGroup(groups, activity),
    [groups, activity]
  );

  const groupLookup: UnsafeRecord<string, FrontendRegistrationGroup> = useMemo(
    () => createLookup(groups),
    [groups]
  );

  const linkedValidation = useMemo(() => {
    return checkLinkedGroups({
      groups: groupLookup,
      id: convertToString(group.id),
      linkedIds: group.links,
      mapping,
      courseId: activity.parentId,
    });
  }, [groupLookup, group.id, group.links, mapping, activity.parentId]);

  // If all groups are hidden but this one is just shown because of student being allocated to it
  // Disable the button.
  const disableOnAllHidden: boolean = useMemo(() => {
    if (mode === "teacher") return false;
    return groups.every((g) => g.hidden || !g.matchDedication);
  }, [groups, mode]);

  const groupName = group.label ?? "[Group name] not found";

  const onDeallocate = useCallback(() => {
    allocationControlActions?.onDeallocate({
      groupId: group.id,
      objectIds: groupIds,
    });
  }, [allocationControlActions, group.id, groupIds]);

  const onAllocate = useCallback(() => {
    allocationControlActions?.onAllocate({
      groupId: group.id,
      newObjectId: group.id,
    });
  }, [allocationControlActions, group.id]);

  const onJoinWaitlist = useCallback(() => {
    allocationControlActions?.onJoinWaitlist(group.id);
  }, [allocationControlActions, group.id]);

  const onLeaveWaitlist = useCallback(() => {
    allocationControlActions?.onLeaveWaitlist(group.id);
  }, [allocationControlActions, group.id]);

  const waitlistProps = useMemo(() => {
    return {
      onJoinWaitlist,
      onLeaveWaitlist,
      isOnWaitlist: isDefined(group) ? group.waitlistPosition > 0 : false,
      enableWaitlist:
        (registration.enableWaitlist ?? false) && group.links.length === 0,
      hasStudentsInWaitlist: isDefined(group)
        ? group.hasStudentsInWaitlist
        : false,
    };
  }, [onJoinWaitlist, onLeaveWaitlist, registration.enableWaitlist, group]);

  return (
    <>
      <AllocationControls
        groupId={group.id}
        translations={translations}
        full={isFull({ groups, mode })}
        mode={mode}
        aria-labelledby={groupControlsLabelledBy(groups)}
        state={activity.state}
        allocationStatus={getAllocationStatus(groups)}
        currentLoadingGroup={allocationControlActions?.currentLoadingGroup ?? 0}
        open={isOpen(groups)}
        onAllocate={onAllocate}
        onDeallocate={onDeallocate}
        clash={hasConflict}
        linkedValidation={linkedValidation}
        blockStudentConflict={registration.blockStudentConflict}
        disabledState={{ disabled: disableOnAllHidden, description: null }}
        activityName={activity.label}
        groupName={groupName}
        waitlistProps={waitlistProps}
      />
    </>
  );
}
